import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useImperativeHandle, useRef } from "react";

import { IconChevronRightSmall } from "@/assets/icons/geist/IconChevronRightSmall";
import type { BaseIconProps } from "@/assets/icons/utils";
import { cn } from "@/components/podkit/lib/cn";

type TabsOrientation = TabsPrimitive.TabsProps["orientation"];

type TabsContextValue = {
    setOrientation: (orientation: TabsOrientation) => void;
};

const TabsContext = createContext<TabsContextValue | null>(null);

const Tabs = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ className, ...props }, forwardRef) => {
    const [orientation, setOrientationState] = React.useState<TabsOrientation>("horizontal");

    const setOrientation = (orientation: TabsOrientation) => {
        setOrientationState(orientation);
    };

    return (
        <TabsContext.Provider value={{ setOrientation }}>
            <TabsPrimitive.Root
                ref={forwardRef}
                orientation={orientation}
                className={cn("@container", className)}
                {...props}
            />
        </TabsContext.Provider>
    );
});

Tabs.displayName = TabsPrimitive.Root.displayName;

/** Scroll distance per arrow click — roughly one tab width. */
const SCROLL_STEP = 100;

type TabsListProps = {
    /**
     * When true, the tab list stays horizontal at all sizes and scrolls
     * when tabs overflow, with arrow buttons at the edges.
     * When false (default), the list switches to vertical layout below
     * the @md container-query breakpoint.
     */
    scrollable?: boolean;
} & React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>;

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
    ({ className, scrollable = false, ...props }, forwardRef) => {
        const ref = useRef<HTMLDivElement>(null);
        const arrowLeftRef = useRef<HTMLDivElement>(null);
        const arrowRightRef = useRef<HTMLDivElement>(null);
        const tabsContext = useContext(TabsContext);

        useImperativeHandle(forwardRef, () => ref.current!);

        // Show/hide arrow buttons based on scroll position (scrollable mode only).
        const applyOverflowIndicators = useCallback(() => {
            if (!scrollable) return;
            const list = ref.current;
            const left = arrowLeftRef.current;
            const right = arrowRightRef.current;
            if (!list || !left || !right) return;

            const { scrollLeft, scrollWidth, clientWidth } = list;
            const threshold = 2;

            left.style.display = scrollLeft > threshold ? "" : "none";
            right.style.display = scrollWidth - scrollLeft - clientWidth > threshold ? "" : "none";
        }, [scrollable]);

        // Scroll the active tab into view (scrollable mode only).
        const scrollActiveIntoView = useCallback(() => {
            if (!scrollable) return;
            const list = ref.current;
            if (!list) return;
            const active = list.querySelector<HTMLElement>('[data-state="active"]');
            if (!active) return;

            const listRect = list.getBoundingClientRect();
            const tabRect = active.getBoundingClientRect();
            if (tabRect.left < listRect.left || tabRect.right > listRect.right) {
                active.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
            }
        }, [scrollable]);

        const scrollBy = useCallback((delta: number) => {
            ref.current?.scrollBy({ left: delta, behavior: "smooth" });
        }, []);

        useEffect(() => {
            const list = ref.current;
            if (!list) return;

            // Orientation detection (kept for Radix keyboard nav).
            const resizeObserver = new ResizeObserver(() => {
                if (!(list instanceof HTMLElement)) return;
                const isVertical = getComputedStyle(list).flexDirection === "column";
                tabsContext?.setOrientation(isVertical ? "vertical" : "horizontal");
                applyOverflowIndicators();
            });
            resizeObserver.observe(list);

            if (!scrollable) {
                return () => resizeObserver.disconnect();
            }

            // scrollable-only observers:

            // Watch for active-tab changes to scroll into view + update indicators.
            const mutationObserver = new MutationObserver(() => {
                scrollActiveIntoView();
                applyOverflowIndicators();
            });
            mutationObserver.observe(list, { attributes: true, attributeFilter: ["data-state"], subtree: true });

            // Track scroll for indicator updates.
            const onScroll = () => applyOverflowIndicators();
            list.addEventListener("scroll", onScroll, { passive: true });

            // Initial state.
            applyOverflowIndicators();
            scrollActiveIntoView();

            return () => {
                resizeObserver.disconnect();
                mutationObserver.disconnect();
                list.removeEventListener("scroll", onScroll);
            };
        }, [tabsContext, scrollable, applyOverflowIndicators, scrollActiveIntoView]);

        // Default mode: vertical-on-mobile, horizontal above @md.
        if (!scrollable) {
            return (
                <TabsPrimitive.List
                    ref={ref}
                    className={cn(
                        "scrollbar-hide flex min-h-8 flex-col justify-stretch gap-0.5 overflow-x-auto rounded-lg border-0.5 border-border-base bg-surface-button-tab-base p-0.5 @md:flex-row @md:justify-between",
                        className,
                    )}
                    {...props}
                />
            );
        }

        // Scrollable mode: always horizontal with arrow buttons.
        return (
            <div className="relative min-w-0 max-w-full">
                <TabsPrimitive.List
                    ref={ref}
                    className={cn(
                        "scrollbar-hide flex min-h-8 flex-row items-stretch gap-0.5 overflow-x-auto rounded-lg border-0.5 border-border-base bg-surface-button-tab-base p-0.5",
                        // Prevent triggers from shrinking or wrapping in scrollable mode.
                        "[&>*]:shrink-0 [&>*]:grow-0 [&>*]:whitespace-nowrap",
                        className,
                    )}
                    {...props}
                />
                {/* Left scroll arrow — gradient fade + pill button inside the tab bar */}
                <div
                    ref={arrowLeftRef}
                    className="absolute inset-y-px left-px z-10 flex w-10 items-center rounded-l-lg bg-gradient-to-r from-surface-button-tab-base from-70% to-transparent"
                    style={{ display: "none" }}
                >
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Scroll tabs left"
                        data-tracking-id="scroll-tabs-left"
                        className="ml-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-button-tab-base text-content-secondary shadow-sm ring-1 ring-border-base transition-colors hover:bg-surface-glass hover:text-content-primary"
                        onClick={() => scrollBy(-SCROLL_STEP)}
                    >
                        <IconChevronRightSmall size="sm" className="rotate-180" />
                    </button>
                </div>
                {/* Right scroll arrow — gradient fade + pill button inside the tab bar */}
                <div
                    ref={arrowRightRef}
                    className="absolute inset-y-px right-px z-10 flex w-10 items-center justify-end rounded-r-lg bg-gradient-to-l from-surface-button-tab-base from-70% to-transparent"
                    style={{ display: "none" }}
                >
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Scroll tabs right"
                        data-tracking-id="scroll-tabs-right"
                        className="mr-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-button-tab-base text-content-secondary shadow-sm ring-1 ring-border-base transition-colors hover:bg-surface-glass hover:text-content-primary"
                        onClick={() => scrollBy(SCROLL_STEP)}
                    >
                        <IconChevronRightSmall size="sm" />
                    </button>
                </div>
            </div>
        );
    },
);
TabsList.displayName = TabsPrimitive.List.displayName;

type TabsTriggerProps = {
    LeadingIcon?: React.ComponentType<BaseIconProps>;
} & React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>;

const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
    ({ className, LeadingIcon, children, asChild, ...props }, ref) => {
        const iconElement = LeadingIcon ? <LeadingIcon size="sm" aria-hidden={true} /> : null;

        const triggerClassName = cn(
            "inline-flex flex-shrink flex-row h-8 min-w-0 flex-grow items-center @md:justify-center gap-2 overflow-hidden rounded-md border-transparent px-1.5 py-1 text-base text-content-strong text-left @md:text-center",
            // hover
            "hover:bg-surface-glass",
            // active
            "data-[state=active]:bg-surface-button-tab-primary data-[state=active]:border-transparent data-[state=active]:text-content-primary data-[state=active]:shadow-sm",
            // disabled
            "disabled:opacity-50 disabled:hover:bg-transparent",
            className,
        );

        if (asChild) {
            return (
                <TabsPrimitive.Trigger ref={ref} className={triggerClassName} asChild {...props}>
                    {children}
                </TabsPrimitive.Trigger>
            );
        }

        return (
            <TabsPrimitive.Trigger ref={ref} className={triggerClassName} {...props}>
                {iconElement}
                <span className="truncate">{children}</span>
            </TabsPrimitive.Trigger>
        );
    },
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(
            "ring-offset-background focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
            "data-[state=active]:duration-300 data-[state=active]:animate-in data-[state=active]:fade-in-0",
            className,
        )}
        {...props}
    />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
export type { TabsTriggerProps };
